import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Heart, Loader2, Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { API, useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LangContext";
import { toast } from "sonner";

export default function ResetPassword() {
  const { lang } = useLang();
  const pl = lang === "pl";
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refreshEntitlements } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(pl ? "Hasło min. 6 znaków" : "Password min. 6 chars");
      return;
    }
    if (password !== confirm) {
      toast.error(pl ? "Hasła nie zgadzają się" : "Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/password/reset`, { token, password });
      // Auto-login the user
      localStorage.setItem("dc_token", res.data.token);
      await refreshEntitlements();
      toast.success(pl ? "Hasło zmienione. Zalogowano." : "Password changed. Logged in.");
      navigate("/dashboard", { replace: true });
      // full reload to pick up user in AuthContext
      setTimeout(() => window.location.reload(), 200);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-[calc(100vh-72px)] items-center justify-center px-4 py-12">
        <div className="dc-card w-full max-w-md p-8 text-center">
          <p className="text-sm text-white">{pl ? "Brak tokenu w linku." : "Missing token in link."}</p>
          <Link to="/forgot-password" className="mt-4 inline-flex items-center gap-1 text-sm dc-gradient-text">
            {pl ? "Wygeneruj nowy link" : "Request a new link"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[calc(100vh-72px)] items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="dc-card w-full max-w-md p-8 sm:p-10 shadow-[0_20px_70px_rgba(253,38,122,0.18)]">
        <div className="mb-7 flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl dc-gradient-bg shadow-[0_12px_40px_rgba(253,38,122,0.4)]">
            <Heart className="h-7 w-7 text-white" fill="white" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-white">
            <span className="dc-gradient-text">{pl ? "Nowe hasło" : "New password"}</span>
          </h1>
          <p className="mt-2 text-sm text-[#a8adbd]">
            {pl ? "Ustaw nowe hasło dla swojego konta." : "Set a new password for your account."}
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-form">
          <div>
            <label className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#a8adbd]">
              <Lock className="h-3.5 w-3.5" />{pl ? "Nowe hasło" : "New password"}
            </label>
            <div className="relative">
              <input data-testid="reset-password-input" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-2xl border border-[#222636] bg-[#0f1117] px-4 py-3.5 pr-12 text-white outline-none focus:border-[#ff4458] focus:ring-2 focus:ring-[#ff4458]/30"
                placeholder="••••••••" autoFocus />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a8adbd] hover:text-white" tabIndex={-1}>
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#a8adbd]">
              <Lock className="h-3.5 w-3.5" />{pl ? "Powtórz hasło" : "Confirm password"}
            </label>
            <input data-testid="reset-confirm-input" type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-2xl border border-[#222636] bg-[#0f1117] px-4 py-3.5 text-white outline-none focus:border-[#ff4458] focus:ring-2 focus:ring-[#ff4458]/30"
              placeholder="••••••••" />
          </div>

          <button type="submit" data-testid="reset-submit-button" disabled={loading || !password || !confirm}
            className="flex w-full items-center justify-center gap-2 rounded-full dc-gradient-bg px-6 py-3.5 font-display font-semibold text-white shadow-[0_10px_30px_rgba(253,38,122,0.35)] transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" fill="white" />}
            <span>{loading ? "..." : (pl ? "Ustaw hasło" : "Set password")}</span>
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-[#a8adbd]">
          <Link to="/login" data-testid="link-to-login" className="inline-flex items-center gap-1 font-semibold dc-gradient-text">
            <ArrowLeft className="h-3.5 w-3.5" />
            {pl ? "Powrót do logowania" : "Back to login"}
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
